import { z } from 'zod'

import type {
  LegacySiteInitializer,
  LegacySiteMutator,
  LegacySiteId,
  LegacySiteCableHistoryId,
  LegacySiteCableHistoryInitializer,
  LegacySiteCorrectionHistoryId,
  LegacySiteCorrectionHistoryInitializer,
  LegacySiteLoggerHistoryId,
  LegacySiteLoggerHistoryInitializer,
  LegacySiteMergeCableHistoryId,
  LegacySiteMergeCableHistoryInitializer,
  LegacySiteHistoryLabels,
  ProjectId,
  OrganizationId,
  SiteWithHistoryView,
  LoggerLastTransmissionView,
  SiteTableView,
  BodySection,
} from '../../index.js'
import {
  legacySiteInitializer,
  legacySiteMutator,
  legacySiteLoggerHistoryInitializer,
  legacySiteCableHistoryInitializer,
  legacySiteCorrectionHistoryInitializer,
  legacySiteMergeCableHistoryInitializer,
  legacySiteLoggerHistoryId,
  legacySiteCableHistoryId,
  legacySiteCorrectionHistoryId,
  projectId,
} from '../../index.js'
import {
  legacySiteHistoryDateSchema,
  legacySiteHistoryLabelsSchema,
  legacySiteLineGapSizeSchema,
  legacySiteNameSchema,
} from '../generics/LegacySitePrimitives.js'
import type LabelSortType from '../schemas/public/LabelSortType.js'
import type LengthUnitType from '../schemas/public/LengthUnitType.js'

// ---------------------------------------------------------------------------
// Extended history types - joined fields from related tables
// ---------------------------------------------------------------------------

export type ExtendedLegacySiteLoggerHistory = {
  id: LegacySiteLoggerHistoryId
  loggerId: number | null
  loggerSerial: string | null // joined from logger_.logger_serial (null when loggerId is null)
  manufacturer: string | null // joined from logger_.manufacturer
  manufacturerDeviceId: string | null // joined from logger_.manufacturer_device_id
  imei: string | null // from legacy_site_logger_history_.imei column
  startedAt: Date | string
  endedAt: Date | string | null
  decode: number[] | null
  dataType: string | null
  labels: LegacySiteHistoryLabels | null
  lastTransmission?: LoggerLastTransmissionView | null
}

export type ExtendedLegacySiteCableHistory = {
  id: LegacySiteCableHistoryId
  cableId: number
  cableSerial: number | null // joined from cable_.cable_serial
  startedAt: Date | string
  endedAt: Date | string | null
  decode: number[] | null
  dataType: string | null
  labels: LegacySiteHistoryLabels | null
  lastCalibration?: Date | string | null
  sdm?: boolean | null // joined from cable_.sdm, indicates if cable is an SDM cable (true) or not (false/null)
  idm?: boolean | null // joined from cable_.idm, indicates if cable is an IDM cable (true) or not (false/null)
}

export type ExtendedLegacySiteMergeCableHistory = {
  id: LegacySiteMergeCableHistoryId
  fromCableHistoryId: LegacySiteCableHistoryId
  toCableHistoryId: LegacySiteCableHistoryId
}

// ---------------------------------------------------------------------------
// ExtendedLegacySite - the API-facing assembled type
// ---------------------------------------------------------------------------

export type ExtendedLegacySite = Omit<
  SiteWithHistoryView,
  'loggerHistory' | 'cableHistory' | 'mergeCableHistory'
> & {
  loggerHistory: ExtendedLegacySiteLoggerHistory[]
  cableHistory: ExtendedLegacySiteCableHistory[]
  mergeCableHistory: ExtendedLegacySiteMergeCableHistory[]
}

// ---------------------------------------------------------------------------
// Initializer / Mutator for API input validation
// ---------------------------------------------------------------------------

export type ExtendedSiteInitializer = LegacySiteInitializer & {
  projectId: ProjectId
}

export type ExtendedSiteTableView = Omit<
  SiteTableView,
  'loggerHistory' | 'cableHistory'
> & {
  loggerHistory: ExtendedLegacySiteLoggerHistory[]
  cableHistory: ExtendedLegacySiteCableHistory[]
}

// ---------------------------------------------------------------------------
// Project report view - table view + cable body/sensorCount for label generation
// ---------------------------------------------------------------------------

export type ProjectReportCableHistory = ExtendedLegacySiteCableHistory & {
  body: BodySection[] | null
  sensorCount: number
}

export type ExtendedSiteProjectReportView = Omit<
  ExtendedSiteTableView,
  'cableHistory'
> & {
  cableHistory: ProjectReportCableHistory[]
  /** From legacy_site_ (not in site_table_view_): V1 label order, as the chart sorts it. */
  labelSettingSortBy: LabelSortType | null
}

export type ExtendedSiteWithHistoryView = Omit<
  SiteWithHistoryView,
  'loggerHistory' | 'cableHistory' | 'mergeCableHistory'
> & {
  loggerHistory: ExtendedLegacySiteLoggerHistory[]
  cableHistory: ExtendedLegacySiteCableHistory[]
  mergeCableHistory: ExtendedLegacySiteMergeCableHistory[]
}

// ---------------------------------------------------------------------------
// Site lines view - full history view + cable body/sensorCount for line generation
// ---------------------------------------------------------------------------

export type SiteLinesCableHistory = ExtendedLegacySiteCableHistory & {
  body: BodySection[] | null
  sensorCount: number
}

export type SiteLinesData = {
  id: LegacySiteId
  siteName: string
  readingsVersion: number | null
  useRecordDate: boolean | null
  showSdmLines: boolean | null
  showIdmLines: boolean | null
  plotSnow: boolean | null
  labelSettingSortBy: LabelSortType | null
  lengthUnits: LengthUnitType
  sonicHeight: number | null
  /** Radar level gauge mount height in cm; null = not set (chart plots distance). */
  radarReferenceHeight: number | null
  projectId: ProjectId
  projectName: string
  organizationId: OrganizationId
  organizationName: string
  loggerHistory: ExtendedLegacySiteLoggerHistory[]
  cableHistory: SiteLinesCableHistory[]
  mergeCableHistory: ExtendedLegacySiteMergeCableHistory[]
  /**
   * Where createSiteV1Lines reads a V1 site's documents from. Set by
   * queryForSiteLines from legacy_site_reading_cutover_.cut_over_at; absent
   * means 'mongo', so hand-built fixtures are unchanged. The audit tool's store
   * mode overrides it on a prefetched SiteLinesData to render both sources.
   * Spec: docs/specs/2026-09-22-v1-era-readings-store.md Section 5.3.
   */
  v1ReadingsSource?: 'mongo' | 'postgres'
}

const embeddedUuidField = z.preprocess(
  (val) => (val === '' ? null : val),
  z.string().optional().nullable(),
)

// showForecast and forecastModelType are deliberately omitted from every
// customer-facing initializer/mutator below. Both are GLOBAL_ADMIN-only
// Forecast Manager fields:
//   * showForecast       -- the forecast DISPLAY permission (#3834)
//   * forecastModelType  -- which model is enrolled to serve the site (#4169)
//   * pageType           -- which page the site renders; 'sea_ice_forecast' is
//                           set only by bin/setSitePageType.ts (#4586)
//   * enhancedVendorDisplay -- unused since #4658 (nothing reads it); the
//                           column stays until an owner-applied migration drops it
//   * layout             -- the signed-in page layout (#4724); only a global
//                           editor changes it, through PUT /site/:id, where the
//                           handler reads it from the raw body itself
//   * flipThermalOrder   -- reverses the assumed order of depthless thermal
//                           sensors on the borehole dashboard; same treatment
//                           as layout (global editors only, raw body)
// Kanel auto-adds both to legacySiteInitializer / legacySiteMutator (they are
// real columns), so they must be stripped here -- otherwise a customer with
// EDIT_SITE/CREATE_SITE could set them via the generic PUT /site/:id or
// POST /site handlers, silently re-routing (or un-routing) their own site's
// forecast. apps/api/src/ez/endpoints/site.ts also strips both at the handler
// layer as defense-in-depth; see
// docs/specs/2026-07-02-per-site-show-forecast.md Section 7 and
// docs/specs/2026-07-28-forecast-approval-model-routing.md D4.
/** Largest distance an int16-mm radar gauge can report (cm). */
export const RADAR_REFERENCE_HEIGHT_MAX_CM = 3276.7

// The generated LegacySite schema allows any number for the radar mount
// height. The four extended schemas below override it so 0, negatives and
// values above what the gauge can measure (a unit mistake) are rejected with a
// 400 / form error instead of reaching the database CHECK.
// Spec: docs/specs/2026-10-19-rlg-units-and-reference-height.md Section 5.1.
export const radarReferenceHeightSchema = z
  .number()
  .positive()
  .max(RADAR_REFERENCE_HEIGHT_MAX_CM)
  .nullable()
  .optional()

// ---------------------------------------------------------------------------
// Write-path overrides (restoring the validation Joi gave before #2950/#3009)
//
// The generated schemas type history `labels` as z.unknown(), coerce dates with
// z.coerce.date() and accept any string/number for siteName/lineGapSize. The
// overrides below are defined once and applied to every schema the site write
// endpoints parse with (POST /site, PUT /site/:id). Generated files under
// src/schemas are not hand-edited.
// ---------------------------------------------------------------------------

const historyDateOverrides = {
  startedAt: legacySiteHistoryDateSchema,
  endedAt: legacySiteHistoryDateSchema.nullable().optional(),
}

// The generated schemas are typed z.Schema<T> (kanel-zod), which hides
// .extend(); the casts below recover the underlying ZodObject.
const withHistoryLabels = (schema: unknown): z.ZodTypeAny =>
  (schema as z.ZodObject<any>).extend({
    ...historyDateOverrides,
    labels: legacySiteHistoryLabelsSchema,
  })

const withHistoryDates = (schema: unknown): z.ZodTypeAny =>
  (schema as z.ZodObject<any>).extend(historyDateOverrides)

const loggerHistoryWriteSchema = withHistoryLabels(
  legacySiteLoggerHistoryInitializer,
)
const cableHistoryWriteSchema = withHistoryLabels(
  legacySiteCableHistoryInitializer,
)
// legacy_site_correction_history_ has CHECK (ended_at IS NULL OR ended_at >
// started_at); reject the violation as a 400 instead of a database 500.
// Cable and logger history have no such constraint.
const correctionHistoryWriteSchema = withHistoryDates(
  legacySiteCorrectionHistoryInitializer,
).superRefine((entry, ctx) => {
  const { startedAt, endedAt } = entry as {
    startedAt: Date
    endedAt?: Date | null
  }
  if (endedAt && startedAt && endedAt <= startedAt) {
    ctx.addIssue({
      code: 'custom',
      message: 'endedAt must be after startedAt',
      path: ['endedAt'],
    })
  }
})

// siteName is required on create and, when sent, must not be blank on update.
// lineGapSize is an INTEGER column.
const siteCreateOverrides = {
  siteName: legacySiteNameSchema,
  lineGapSize: legacySiteLineGapSizeSchema,
}
const siteUpdateOverrides = {
  siteName: legacySiteNameSchema.optional(),
  lineGapSize: legacySiteLineGapSizeSchema,
}

export const extendedSiteInitializer = (
  legacySiteInitializer as unknown as z.ZodObject<any>
)
  .omit({
    showForecast: true,
    forecastModelType: true,
    pageType: true,
    enhancedVendorDisplay: true,
    layout: true,
    flipThermalOrder: true,
  })
  .extend({
    projectId: projectId,
    embeddedUuid: embeddedUuidField,
    showIdmLines: z.boolean().optional(),
    radarReferenceHeight: radarReferenceHeightSchema,
    ...siteCreateOverrides,
  }) as unknown as z.Schema<ExtendedSiteInitializer>

// ---------------------------------------------------------------------------
// Extended initializer with history - used by the POST /site endpoint
// ---------------------------------------------------------------------------

export type ExtendedSiteWithHistoryInitializer = LegacySiteInitializer & {
  projectId: ProjectId
  loggerHistory?: LegacySiteLoggerHistoryInitializer[]
  cableHistory?: LegacySiteCableHistoryInitializer[]
}

export const extendedSiteWithHistoryInitializer = (
  legacySiteInitializer as unknown as z.ZodObject<any>
)
  .omit({
    showForecast: true,
    forecastModelType: true,
    pageType: true,
    enhancedVendorDisplay: true,
    layout: true,
    flipThermalOrder: true,
  })
  .extend({
    projectId: projectId,
    embeddedUuid: embeddedUuidField,
    showIdmLines: z.boolean().optional(),
    radarReferenceHeight: radarReferenceHeightSchema,
    ...siteCreateOverrides,
    loggerHistory: z.array(loggerHistoryWriteSchema).max(100).optional(),
    cableHistory: z.array(cableHistoryWriteSchema).max(100).optional(),
  }) as unknown as z.Schema<ExtendedSiteWithHistoryInitializer>

export type ExtendedSiteMutator = LegacySiteMutator

export const extendedSiteMutator = (
  legacySiteMutator as unknown as z.ZodObject<any>
)
  .omit({
    showForecast: true,
    forecastModelType: true,
    pageType: true,
    enhancedVendorDisplay: true,
    layout: true,
    flipThermalOrder: true,
  })
  .extend({
    embeddedUuid: embeddedUuidField,
    showIdmLines: z.boolean().optional(),
    radarReferenceHeight: radarReferenceHeightSchema,
    ...siteUpdateOverrides,
  }) as unknown as z.Schema<ExtendedSiteMutator>

// ---------------------------------------------------------------------------
// Extended mutator with history - used by the PUT /site/:id endpoint
// ---------------------------------------------------------------------------

export type ExtendedSiteWithHistoryMutator = LegacySiteMutator & {
  loggerHistory?: LegacySiteLoggerHistoryInitializer[]
  cableHistory?: LegacySiteCableHistoryInitializer[]
  correctionHistory?: LegacySiteCorrectionHistoryInitializer[]
  mergeCableHistory?: LegacySiteMergeCableHistoryInitializer[]
  deletedLoggerHistoryIds?: LegacySiteLoggerHistoryId[]
  deletedCableHistoryIds?: LegacySiteCableHistoryId[]
  deletedCorrectionHistoryIds?: LegacySiteCorrectionHistoryId[]
}

export const extendedSiteWithHistoryMutator = (
  legacySiteMutator as unknown as z.ZodObject<any>
)
  .omit({
    showForecast: true,
    forecastModelType: true,
    pageType: true,
    enhancedVendorDisplay: true,
    layout: true,
    flipThermalOrder: true,
  })
  .extend({
    embeddedUuid: embeddedUuidField,
    showIdmLines: z.boolean().optional(),
    radarReferenceHeight: radarReferenceHeightSchema,
    ...siteUpdateOverrides,
    loggerHistory: z.array(loggerHistoryWriteSchema).max(100).optional(),
    cableHistory: z.array(cableHistoryWriteSchema).max(100).optional(),
    correctionHistory: z
      .array(correctionHistoryWriteSchema)
      .max(100)
      .optional(),
    mergeCableHistory: z
      .array(legacySiteMergeCableHistoryInitializer as unknown as z.ZodTypeAny)
      .max(100)
      .optional(),
    deletedLoggerHistoryIds: z
      .array(legacySiteLoggerHistoryId)
      .max(100)
      .optional(),
    deletedCableHistoryIds: z
      .array(legacySiteCableHistoryId)
      .max(100)
      .optional(),
    deletedCorrectionHistoryIds: z
      .array(legacySiteCorrectionHistoryId)
      .max(100)
      .optional(),
  }) as unknown as z.Schema<ExtendedSiteWithHistoryMutator>
